// 🔧 volunteer_time_slot_defaults.js
// ✅ Purpose: Generates volunteer time slots for Demo Engine events
// ✅ Features:
// - Builds realistic volunteer shifts for each activity
// - Uses event dates from DemoContext
// - Adjusts staffing based on demo size
// - No Firestore writes
// - No DemoContext mutation
// - Fully block-commented for clarity and future devs

// =====================================================
// Build volunteer time slots
// =====================================================

export const buildVolunteerTimeSlots = ({
  industryTemplate = "car_show",
  demoSize = "medium",
  activities = [],
  demoContext,
} = {}) => {
  console.log("🕒 Building volunteer time slots.");

  const eventStart =
    demoContext?.eventResult?.eventDoc?.startDate?.toDate?.() ||
    demoContext?.eventResult?.eventDoc?.startDate ||
    demoContext?.event?.startDate ||
    new Date();

  const demoUsers = demoContext?.users || [];
  const demoCreator = demoUsers.find((user) => user?.demoCreator === true);

  // =====================================================
  // Determine staffing multiplier
  // =====================================================

  let volunteerCount = 2;

  switch (demoSize) {
    case "small":
      volunteerCount = 1;
      break;

    case "large":
      volunteerCount = 4;
      break;

    case "xlarge":
      volunteerCount = 6;
      break;

    default:
      volunteerCount = 2;
  }

  const slots = [];

  activities.forEach((activity, activityIndex) => {

    // The first seeded shift belongs to the Experience creator. This makes
    // the administrator's Volunteer view usable immediately after creation.
    const morningUser = activityIndex === 0 && demoCreator
      ? demoCreator
      : demoUsers[(activityIndex * 2) % Math.max(demoUsers.length, 1)];

    const morningAssignments = morningUser
      ? [
          {
            uid: morningUser.id,
            firstName: morningUser.firstName || "",
            lastName: morningUser.lastName || "",
            displayName: morningUser.displayName || "Demo Volunteer",
            email: morningUser.email || "",
          },
        ]
      : [];

    // Morning Shift
    slots.push({
      activityId: activity.id,
      title: `${activity.title} - Morning`,
      description: activity.description,
      displayOrder: activity.displayOrder,
      requiredVolunteers: volunteerCount,
      assignedUsers: morningAssignments,
      assignedUserIds: morningAssignments.map((user) => user.uid),
      startDateTime: buildDate(eventStart, 8, 0),
      endDateTime: buildDate(eventStart, 12, 0),
    });

    // Afternoon Shift
    slots.push({
      activityId: activity.id,
      title: `${activity.title} - Afternoon`,
      description: activity.description,
      displayOrder: activity.displayOrder + 1,
      requiredVolunteers: volunteerCount,
      assignedUsers: [],
      assignedUserIds: [],
      startDateTime: buildDate(eventStart, 12, 0),
      endDateTime: buildDate(eventStart, 16, 0),
    });

    // Evening shift only for larger events
    if (
      demoSize === "large" ||
      demoSize === "xlarge"
    ) {
      slots.push({
        activityId: activity.id,
        title: `${activity.title} - Evening`,
        description: activity.description,
        displayOrder: activity.displayOrder + 2,
        requiredVolunteers: volunteerCount,
        assignedUsers: [],
        assignedUserIds: [],
        startDateTime: buildDate(eventStart, 16, 0),
        endDateTime: buildDate(eventStart, 20, 0),
      });
    }
  });

  console.log("✅ Volunteer time slots created.", {
    activityCount: activities.length,
    slotCount: slots.length,
    industryTemplate,
    demoSize,
  });

  return slots;
};

// =====================================================
// Helper
// =====================================================

function buildDate(baseDate, hour, minute) {
  const date = new Date(baseDate);

  date.setHours(hour);
  date.setMinutes(minute);
  date.setSeconds(0);
  date.setMilliseconds(0);

  return date;
}
